import { isActionWhitelisted, requiresConfirmation } from 'layout/widgets/adaChat/actionPermissions'
import { createTemplateFromSelectedSystem } from 'layout/widgets/adaChat/actions'
import {
  getSelectedSystem,
  validateActionRequirementsOrThrow,
} from 'layout/widgets/adaChat/utils/validateActionRequirements'
import { extractTemplateData } from 'projectSections/sections/design/sideDrawer/autoDesign/V2/templateCreation'
import { LiveFunctionCall } from '../GeminiLiveAPI/lib/multimodal-live-types'
import { CallServerToolCall } from './callServerTool'

interface SaveSystemDesignAsTemplateArgs {
  name: string
  description?: string | null
  project_id?: number | null
  confirmed?: boolean // Optional flag to indicate user has confirmed
}

export const TOOL_NAME = 'save_system_design_as_template'

function transformTemplateForAdaTool(template: any): any {
  const extracted = extractTemplateData(template)

  return {
    name: extracted.name,
    description: extracted.description,
    components_and_quantities:
      extracted.components_and_quantities.length > 0 ? extracted.components_and_quantities : null,
    design_type: extracted.design_type,
    constraints: extracted.constraints.length > 0 ? extracted.constraints : null,
    pricing_scheme_id: extracted.pricing_scheme_id || null,
    mounting_config: extracted.mounting_config ?? null,
    inverter_design_assistant: extracted.inverter_design_assistant,
    battery_design_assistant: extracted.battery_design_assistant,
    orientation_options: extracted.orientation_options,
    azimuth_from_to: extracted.azimuth_from_to,
    payment_options_override: extracted.payment_options_override || null,
    incentives_override: extracted.incentives_override || null,
    costing_override: extracted.costing_override || null,
    commission_override_id: extracted.commission_override_id || null,
    battery_control_scheme_override: extracted.battery_control_scheme_override || null,
  }
}

export async function SaveSystemDesignAsTemplateToolCall(
  functionCall: LiveFunctionCall,
  history: any,
  orgId: number | undefined
) {
  const args = functionCall.args as SaveSystemDesignAsTemplateArgs

  // Check if this action requires confirmation
  // In voice mode, Ada should ask for confirmation before calling this tool
  // However, if the action is whitelisted, skip the confirmation check
  const isWhitelisted = isActionWhitelisted('saveSystemDesignAsTemplate')

  if (requiresConfirmation('saveSystemDesignAsTemplate') && !isWhitelisted) {
    // Check if confirmation was explicitly provided in args
    if (!args.confirmed) {
      throw new Error(
        `This action requires confirmation. Please ask the user: "Would you like me to save this system as a template called '${args.name}'?" and wait for their confirmation before proceeding.`
      )
    }
  }

  // Validate action requirements (design tab, studio loaded, selected system)
  const pathname = window.location.pathname
  const selectedSystem = getSelectedSystem()
  validateActionRequirementsOrThrow('saveSystemDesignAsTemplate', pathname, selectedSystem)

  const template = createTemplateFromSelectedSystem(args.name, args.description)

  if (!orgId) {
    throw new Error('Organization ID is required to save template')
  }

  const templateData = transformTemplateForAdaTool(template)

  const serverToolCall: LiveFunctionCall = {
    ...functionCall,
    name: 'create_or_update_system_template',
    args: templateData,
  }

  return await CallServerToolCall(serverToolCall, history, orgId)
}
